import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronLeft, CalendarDays, X } from "lucide-react";
import {
  JALALI_MONTHS, JALALI_WEEKDAYS, dateToJalali, jalaliToDate, jalaaliMonthLength,
  jalaliWeekdayIndex, formatJalaliDateTime, faDigitsStr,
} from "../lib/jalali";

/**
 * Compact Jalali (Shamsi) date + time picker.
 * value: JS Date | null
 * onChange: (date: Date | null) => void
 */
export default function JalaliDateTimePicker({ value, onChange, placeholder = "انتخاب تاریخ و ساعت", testId }) {
  const [open, setOpen] = useState(false);
  const base = value || new Date();
  const initial = dateToJalali(base);
  const [viewJy, setViewJy] = useState(initial.jy);
  const [viewJm, setViewJm] = useState(initial.jm);
  const [hh, setHh] = useState(value ? initial.hh : 0);
  const [mm, setMm] = useState(value ? initial.mm : 0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    const j = dateToJalali(value || new Date());
    setViewJy(j.jy); setViewJm(j.jm);
    if (value) { setHh(j.hh); setMm(j.mm); }
  }, [open]); // eslint-disable-line

  const selJ = value ? dateToJalali(value) : null;

  const monthLen = jalaaliMonthLength(viewJy, viewJm);
  const firstOfMonth = jalaliToDate(viewJy, viewJm, 1);
  const leadingBlanks = jalaliWeekdayIndex(firstOfMonth);
  const cells = [...Array(leadingBlanks).fill(null), ...Array.from({ length: monthLen }, (_, i) => i + 1)];

  const gotoMonth = (delta) => {
    let jm = viewJm + delta, jy = viewJy;
    if (jm < 1) { jm = 12; jy -= 1; }
    if (jm > 12) { jm = 1; jy += 1; }
    setViewJy(jy); setViewJm(jm);
  };

  const pickDay = (day) => {
    if (!day) return;
    const d = jalaliToDate(viewJy, viewJm, day, hh, mm);
    onChange(d);
  };

  const today = new Date();
  const todayJ = dateToJalali(today);

  const clear = (e) => { e.stopPropagation(); onChange(null); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" data-testid={testId} onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs bg-white hover:border-teal-400 focus:border-teal-500 outline-none min-w-[168px]">
        <CalendarDays size={14} className="text-slate-400 shrink-0" />
        <span className={value ? "text-slate-700 tabular-nums" : "text-slate-400"}>
          {value ? formatJalaliDateTime(value) : placeholder}
        </span>
        {value && (
          <span onClick={clear} className="mr-auto text-slate-300 hover:text-rose-500"><X size={13} /></span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-3 w-[260px]" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => gotoMonth(-1)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronRight size={16} /></button>
            <div className="text-xs font-semibold text-slate-700">{JALALI_MONTHS[viewJm - 1]} {faDigitsStr(viewJy)}</div>
            <button type="button" onClick={() => gotoMonth(1)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronLeft size={16} /></button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {JALALI_WEEKDAYS.map((w) => (
              <div key={w} className="text-[10px] text-slate-400 text-center">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 mb-3">
            {cells.map((day, i) => {
              const isSel = day && selJ && selJ.jy === viewJy && selJ.jm === viewJm && selJ.jd === day;
              const isToday = day && todayJ.jy === viewJy && todayJ.jm === viewJm && todayJ.jd === day;
              return (
                <button type="button" key={i} disabled={!day} onClick={() => pickDay(day)}
                  className={`h-7 rounded-lg text-[11px] tabular-nums transition-colors
                    ${!day ? "invisible" : isSel ? "bg-teal-600 text-white font-semibold" : isToday ? "bg-teal-50 text-teal-700 font-semibold" : "text-slate-600 hover:bg-slate-100"}`}>
                  {day ? faDigitsStr(day) : ""}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
            <div className="flex items-center gap-1 text-xs">
              <input type="number" min={0} max={23} value={hh}
                onChange={(e) => setHh(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                className="w-11 px-1.5 py-1 rounded-lg border border-slate-300 text-center tabular-nums" />
              <span className="text-slate-400">:</span>
              <input type="number" min={0} max={59} value={mm}
                onChange={(e) => setMm(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                className="w-11 px-1.5 py-1 rounded-lg border border-slate-300 text-center tabular-nums" />
              <span className="text-slate-400 mr-1">ساعت</span>
            </div>
            <button type="button"
              onClick={() => {
                if (selJ) { onChange(jalaliToDate(selJ.jy, selJ.jm, selJ.jd, hh, mm)); }
                else { onChange(jalaliToDate(viewJy, viewJm, cells.find(Boolean) || 1, hh, mm)); }
                setOpen(false);
              }}
              className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1 rounded-lg text-[11px] font-semibold">تأیید</button>
          </div>
        </div>
      )}
    </div>
  );
}
